<template>
  <div
    ref="mapContainer"
    class="flow-map flow-component absolute bottom-24 right-4 z-50 block cursor-default overflow-hidden bg-slate-200 shadow-md ring-1 ring-slate-700/50 dark:bg-[#040406] dark:ring-slate-600/50"
    :style="mapDimensions"
    @click="handleClick"
  >
    <div
      v-drag="dragHandler"
      class="absolute cursor-move bg-red-500/15 ring-1 ring-red-500"
      :style="overlayTransforms"
    />
    <div
      :key="forceUpdate"
      class="pointer-events-none absolute inset-0 flex items-center justify-center"
      :style="mapTransforms"
    >
      <div
        ref="nodesContainer"
        class="relative"
      >
        <div class="flowbuilder-nodes flex flex-col items-center">
          <RootNode :node="funnel" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RootNode } from '@rightmessage/flow-builder'

const props = defineProps({
  flowBuilderDimensions: Object,
  flowBuilderContainerDimensions: Object,
  contentDimensions: Object,
  transform: Object,
  funnel: Object,
  small: { type: Boolean, default: false }
})

const defaultWidth = props.small ? 200 : 350
const defaultHeight = props.small ? 150 : 200
const mapContainer = ref(null)
const nodesContainer = ref(null)

const isDragging = ref(false)

// Force a re-render when content dimensions change significantly
const forceUpdate = ref(0)

// Debounce content dimension changes to avoid excessive re-renders
const debouncedUpdate = useDebounceFn(() => {
  forceUpdate.value++
}, 100)

// Watch for content dimension changes and force update with debounce
watch(() => props.contentDimensions, () => {
  debouncedUpdate()
}, { deep: true })

const dragHandler = (data) => {
  isDragging.value = true

  const mainZoom = props.transform?.scale || 1
  const miniMapScale = scale.value

  // Convert mini-map drag delta to viewport-space transform
  // The ratio converts mini-map pixels to viewport transform units
  const dragToTransformRatio = mainZoom / miniMapScale

  props.transform.x = props.transform.x - data.direction[0] * dragToTransformRatio
  props.transform.y = props.transform.y - data.direction[1] * dragToTransformRatio

  // Reset the dragging state after a short delay
  setTimeout(() => {
    isDragging.value = false
  }, 100)
}

const handleClick = (e) => {
  if (isDragging.value) return

  const mainZoom = props.transform?.scale || 1
  const miniMapScale = scale.value

  const effectiveWidth = props.contentDimensions?.width || props.flowBuilderContainerDimensions.width
  const effectiveHeight = props.contentDimensions?.height || props.flowBuilderContainerDimensions.height

  const scaledContentWidth = effectiveWidth * miniMapScale
  const scaledContentHeight = effectiveHeight * miniMapScale

  // Click position relative to mini-map
  const clickX = e.offsetX
  const clickY = e.offsetY

  // Centering offsets
  const offsetLeft = (defaultWidth - scaledContentWidth) / 2
  const offsetTop = (defaultHeight - scaledContentHeight) / 2

  // Click position relative to content origin in mini-map space
  const miniMapContentX = clickX - offsetLeft
  const miniMapContentY = clickY - offsetTop

  // Convert to content-space coordinates
  const contentX = miniMapContentX / miniMapScale
  const contentY = miniMapContentY / miniMapScale

  // Calculate transform to center viewport on this content point
  const halfViewportWidth = props.flowBuilderDimensions.width / 2
  const halfViewportHeight = props.flowBuilderDimensions.height / 2

  // Transform formula: viewport center should be at content point
  // screenCenter = contentPoint * mainZoom + transform
  // transform = screenCenter - contentPoint * mainZoom
  props.transform.x = halfViewportWidth - contentX * mainZoom
  props.transform.y = halfViewportHeight - contentY * mainZoom
}

const mapDimensions = computed(() => {
  return {
    width: `${defaultWidth}px`,
    height: `${defaultHeight}px`
  }
})

const scale = computed(() => {
  if (!props.flowBuilderContainerDimensions) {
    return 1
  }
  
  // Use content dimensions if available, otherwise fall back to container dimensions
  const effectiveWidth = props.contentDimensions?.width || props.flowBuilderContainerDimensions.width
  const effectiveHeight = props.contentDimensions?.height || props.flowBuilderContainerDimensions.height
  
  const xScale = (defaultWidth - 10) / effectiveWidth
  const yScale = (defaultHeight - 10) / effectiveHeight
  return Math.min(xScale, yScale)
})

const mapTransforms = computed(() => {
  if (!scale.value) {
    return { transform: 'scale(1)', transformOrigin: 'center' }
  }

  return {
    transform: `scale(${scale.value})`,
    transformOrigin: 'center'
  }
})

const overlayTransforms = computed(() => {
  if (!props.flowBuilderDimensions || !props.flowBuilderContainerDimensions) {
    return { left: '0px', top: '0px', width: '0px', height: '0px' }
  }

  const mainZoom = props.transform?.scale || 1
  const miniMapScale = scale.value

  // Use content dimensions for accurate scaling
  const effectiveWidth = props.contentDimensions?.width || props.flowBuilderContainerDimensions.width
  const effectiveHeight = props.contentDimensions?.height || props.flowBuilderContainerDimensions.height

  // Content dimensions scaled to mini-map space
  const scaledContentWidth = effectiveWidth * miniMapScale
  const scaledContentHeight = effectiveHeight * miniMapScale

  // Viewport size in content-space, then scaled to mini-map space
  // At zoom level z, viewport shows (viewportSize / z) content pixels
  const viewportContentWidth = props.flowBuilderDimensions.width / mainZoom
  const viewportContentHeight = props.flowBuilderDimensions.height / mainZoom

  const width = viewportContentWidth * miniMapScale
  const height = viewportContentHeight * miniMapScale

  // Centering offsets (content is centered in mini-map)
  const offsetLeft = (defaultWidth - scaledContentWidth) / 2
  const offsetTop = (defaultHeight - scaledContentHeight) / 2

  // Convert pan offset from viewport-space to content-space, then to mini-map space
  // Ratio combines both transformations: miniMapScale / mainZoom
  const zoomToMiniMapRatio = miniMapScale / mainZoom

  const left = offsetLeft - (props.transform?.x || 0) * zoomToMiniMapRatio
  const top = offsetTop - (props.transform?.y || 0) * zoomToMiniMapRatio

  return {
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`
  }
})

provide('preview', 'map')
</script>
